import { useState } from 'react';
import {
  useGetPromptsQuery,
  useRegenerateAudioMutation,
  useSavePromptMutation,
  type PromptRecord
} from '@/store/configApi';
import { VoiceSettings } from '@/components/forms/VoiceSettings';
import { PronunciationEditor } from '@/components/forms/PronunciationEditor';
import { useActiveLocation } from '@/hooks/useActiveLocation';
import { useAppDispatch } from '@/store';
import { pushToast } from '@/store/uiSlice';
import { LocationPicker } from '@/components/layout/LocationPicker';
import { Button, Panel, SkeletonRows } from '@/components/ui';
import { useState as useTabState } from 'react';

/**
 * This screen is the complete vocabulary of the phone line. Every phrase it can
 * speak appears here, grouped by when it is used. If a phrase is not on this
 * page and is not read from the menu or hours, the line cannot say it.
 */
const GROUPS: { heading: string; blurb: string; keys: string[] }[] = [
  {
    heading: 'Answering the phone',
    blurb: 'The first thing a caller hears, and what they hear outside your hours.',
    keys: ['GREETING', 'CLOSED', 'HOLIDAY', 'MAIN_MENU', 'GOODBYE']
  },
  {
    heading: 'When it cannot help',
    blurb:
      'Used when the line does not recognise a question, or cannot make out what was said. These are the phrases that keep it from guessing.',
    keys: ['FALLBACK_UNKNOWN', 'LOW_CONFIDENCE', 'TRANSFER', 'TRANSFER_UNAVAILABLE']
  },
  {
    heading: 'Taking an order',
    blurb: 'Only used when phone ordering is switched on for this location.',
    keys: [
      'ORDER_START',
      'ASK_NEXT_ITEM',
      'ITEM_ADDED',
      'ITEM_NOT_FOUND',
      'ITEM_UNAVAILABLE',
      'ASK_MODIFIER',
      'MODIFIER_NOT_RECOGNISED',
      'ORDER_EMPTY'
    ]
  },
  {
    heading: 'Finishing an order',
    blurb: 'Read back, customer details, and confirmation.',
    keys: [
      'ORDER_REVIEW',
      'ORDER_CONFIRM_PROMPT',
      'ASK_NAME',
      'ASK_PHONE',
      'ASK_FULFILLMENT',
      'ASK_PICKUP_TIME',
      'ORDER_CONFIRMED',
      'ORDER_CANCELLED',
      'ORDER_FAILED'
    ]
  }
];

const TABS = [
  { id: 'sound', label: 'How it sounds' },
  { id: 'words', label: 'What it says' },
  { id: 'pronunciation', label: 'Pronunciation' }
] as const;

export function VoicePage() {
  const { locationId } = useActiveLocation();
  const [tab, setTab] = useTabState<(typeof TABS)[number]['id']>('sound');
  const { data, isLoading } = useGetPromptsQuery(locationId, { skip: !locationId });
  const [regenerate, regenerating] = useRegenerateAudioMutation();
  const dispatchToast = useAppDispatch();

  const byKey = new Map((data?.data ?? []).map((prompt) => [prompt.key, prompt]));

  const rebuildAudio = async () => {
    const result = await regenerate({ locationId });
    dispatchToast(
      'error' in result
        ? pushToast('Could not regenerate audio.', 'error')
        : pushToast(result.data.message, 'success')
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-display">Voice</h1>
        <LocationPicker />
        <Button
          variant="secondary"
          className="ml-auto"
          loading={regenerating.isLoading}
          onClick={rebuildAudio}
        >
          Regenerate audio
        </Button>
      </div>

      <div className="flex flex-wrap gap-2">
        {TABS.map((entry) => (
          <button
            key={entry.id}
            onClick={() => setTab(entry.id)}
            className={`rounded-sm border px-2.5 py-1 text-small transition-colors ${
              tab === entry.id
                ? 'border-ink bg-ink text-white'
                : 'border-rule bg-white text-slate hover:text-ink'
            }`}
          >
            {entry.label}
          </button>
        ))}
      </div>

      {tab === 'sound' ? <VoiceSettings locationId={locationId} /> : null}
      {tab === 'pronunciation' ? <PronunciationEditor locationId={locationId} /> : null}

      {tab !== 'words' ? null : isLoading ? (
        <Panel>
          <SkeletonRows rows={8} />
        </Panel>
      ) : (
        GROUPS.map((group) => (
          <Panel key={group.heading} title={group.heading}>
            <p className="border-b border-rule px-4 py-2.5 text-small text-slate">
              {group.blurb}
            </p>
            <ul className="divide-y divide-rule">
              {group.keys.map((key) => {
                const prompt = byKey.get(key);
                return prompt ? (
                  <PromptRow key={key} locationId={locationId} prompt={prompt} />
                ) : null;
              })}
            </ul>
          </Panel>
        ))
      )}

      {tab === 'words' ? (
        <p className="max-w-2xl text-small text-slate">
          This is everything your line is able to say. Answers about hours, prices and menu
          items are read from your own data, so they are not listed here — and nothing
          outside this page and that data can ever be spoken.
        </p>
      ) : null}
    </div>
  );
}

/** Human labels: operators should never see the internal prompt keys. */
const LABELS: Record<string, string> = {
  GREETING: 'Greeting',
  CLOSED: 'Closed right now',
  HOLIDAY: 'Closed for a holiday',
  MAIN_MENU: 'What it can help with',
  GOODBYE: 'Saying goodbye',
  FALLBACK_UNKNOWN: 'Question it was not set up for',
  LOW_CONFIDENCE: 'Could not make out the caller',
  TRANSFER: 'Passing the caller to staff',
  TRANSFER_UNAVAILABLE: 'Nobody available to take the call',
  ORDER_START: 'Starting an order',
  ASK_NEXT_ITEM: 'Asking if they want anything else',
  ITEM_ADDED: 'Item added',
  ITEM_NOT_FOUND: 'Item not on the menu',
  ITEM_UNAVAILABLE: 'Item sold out',
  ASK_MODIFIER: 'Asking for a choice, like spice level',
  MODIFIER_NOT_RECOGNISED: 'Did not catch the choice',
  ORDER_EMPTY: 'Nothing on the order yet',
  ORDER_REVIEW: 'Reading the order back',
  ORDER_CONFIRM_PROMPT: 'Asking them to confirm',
  ASK_NAME: 'Asking for a name',
  ASK_PHONE: 'Asking for a phone number',
  ASK_FULFILLMENT: 'Pickup or delivery',
  ASK_PICKUP_TIME: 'Asking when they want it',
  ORDER_CONFIRMED: 'Order confirmed',
  ORDER_CANCELLED: 'Order cancelled',
  ORDER_FAILED: 'Order could not be placed'
};

function PromptRow({ locationId, prompt }: { locationId: string; prompt: PromptRecord }) {
  const dispatch = useAppDispatch();
  const [save, { isLoading }] = useSavePromptMutation();
  const [text, setText] = useState(prompt.text);
  const [editing, setEditing] = useState(false);

  const dirty = text.trim() !== prompt.text.trim();

  const submit = async () => {
    const result = await save({ locationId, key: prompt.key, text: text.trim() });
    if ('error' in result) {
      dispatch(pushToast('Could not save that phrase.', 'error'));
      return;
    }
    setEditing(false);
    dispatch(pushToast('Phrase saved. New audio is generated on the next call.', 'success'));
  };

  return (
    <li className="px-4 py-3">
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-small font-medium text-ink">{LABELS[prompt.key] ?? prompt.key}</p>

          {editing ? (
            <div className="mt-2 space-y-2">
              <textarea
                rows={2}
                value={text}
                onChange={(event) => setText(event.target.value)}
                className="w-full rounded border border-rule bg-white px-3 py-2 text-small"
                aria-label={LABELS[prompt.key] ?? prompt.key}
              />
              <div className="flex items-center gap-2">
                <Button loading={isLoading} disabled={!dirty} onClick={submit}>
                  Save
                </Button>
                <button
                  onClick={() => {
                    setText(prompt.text);
                    setEditing(false);
                  }}
                  className="text-small text-slate hover:text-ink"
                >
                  Cancel
                </button>
                {prompt.customised ? (
                  <button
                    onClick={() => setText(prompt.defaultText)}
                    className="ml-auto text-small text-slate hover:text-ink"
                  >
                    Use the standard wording
                  </button>
                ) : null}
              </div>
            </div>
          ) : (
            <p className="mt-0.5 text-small text-slate">{prompt.text}</p>
          )}
        </div>

        {editing ? null : (
          <button
            onClick={() => setEditing(true)}
            className="shrink-0 text-small text-jade underline underline-offset-2"
          >
            Edit
          </button>
        )}
      </div>
    </li>
  );
}
